// new-critical-date.component.ts
import { ChangeDetectorRef, Component, OnInit, Inject, inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormControl } from '@angular/forms';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { firstValueFrom, Observable, map, startWith, combineLatest } from 'rxjs';

import { MatGridListModule } from '@angular/material/grid-list';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatNativeDateModule } from '@angular/material/core';

import { DateRecurrenceEditorComponent } from '../../shared/components/date-recurrence-editor/date-recurrence-editor.component';
import { ReminderTestPanelComponent } from '../../shared/components/reminder-test-panel/reminder-test-panel.component';

import { CommonModule } from '@angular/common';

import { CriticalDatesService } from '../../service/entity-services/critical-dates.service';
import { UsersService } from '../../service/entity-services/users.service';
import { DateSheetsService } from '../../service/google-sheets/date-sheets.service';
import { EmailService } from '../../service/communications/email.service';
import { CriticalDate, CriticalDateDialogData, DateRecurrence } from '../../objects/critical-dates';
import { anchorTo, describeRecurrence, nextOccurrence } from '../../shared/utils/date-recurrence';
import { environment } from '../../../environments/environment';
import { entityPathFor } from '../../shared/utils/entity-route';

@Component({
  selector: 'app-new-critical-date',
  templateUrl: './new-critical-date.component.html',
  styleUrls: ['./new-critical-date.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatGridListModule,
    MatFormFieldModule,
    MatDatepickerModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatAutocompleteModule,
    MatNativeDateModule,
    DateRecurrenceEditorComponent,
    ReminderTestPanelComponent
  ]
})
export class NewCriticalDateComponent implements OnInit {
  // Form data
  currentCriticalDate!: CriticalDate;
  waiting = false;
  
  // UI data
  title = 'Add Important Date';
  message = 'Create a new important date or reminder';
  
  // Dropdown/autocomplete data
  allDateTypes: string[] = [];
  filteredDateTypes!: Observable<string[]>;
  typeControl = new FormControl('');
  
  allUsers: any[] = [];
  currentUserEmail: string | null = null;

  okToLeaveIntakeForm: boolean = true;

  typeSearchControl = new FormControl('');

  formErrors: string[] = [];
  hasValidationErrors = false;

  /**
   * Whether the "Repeats" control is offered at all.
   *
   * Read as "off unless explicitly true", so an org that has never heard of the
   * flag is safe. It stays off wherever the roll-forward sweep in
   * hc-all-orgs-scheduled-ops is not deployed — a toggle that promises a repeat
   * and silently never fires is worse than no toggle.
   *
   * Gates the UI only. A record that already carries `recurrence` keeps it
   * through an edit here: the field is never cleared, just not shown.
   */
  readonly showRecurrence = (environment as any).recurringImportantDates === true;

  /**
   * SANDBOX-ONLY reminder test panel (send a test reminder now, "pretend today
   * is…"). Only environment.sandbox-ng.ts sets this; off everywhere else. Shown
   * only for a date that has been saved, since the tool reads the saved record.
   */
  readonly showReminderTest = (environment as any).reminderTestTools === true;

  /** Zoneless app: state set after an await must notify Angular explicitly. */
  private cdrZoneless = inject(ChangeDetectorRef);


  constructor(
    private criticalDatesService: CriticalDatesService,
    private usersService: UsersService,
    private dateSheetsService: DateSheetsService,
    private emailService: EmailService,
    private dialogRef: MatDialogRef<NewCriticalDateComponent>,
    @Inject(MAT_DIALOG_DATA) private data: CriticalDateDialogData
  ) {
    this.initializeComponent();
  }

  private initializeComponent(): void {
    // Set title and message from dialog data
    this.title = this.data.title || 'Add Important Date';
    this.message = this.data.message || 'Create a new important date or reminder';

    // Initialize critical date object
    this.currentCriticalDate = this.createCriticalDateFromData();
    // A date that already repeats with no notification on is the silent series this
    // gate exists to prevent. Switch it off in the form and say so; nothing changes
    // on the record unless the user saves.
    this.enforceRecurrenceNeedsNotification();
    
    // DON'T setup autocomplete here - do it in ngOnInit after data loads
    // this.filteredDateTypes will be set up in ngOnInit after loadDateTypes()
  }

  private createCriticalDateFromData(): CriticalDate {

    // console.log('*** data: ', this.data);

    // Handle wrapped format: { criticalDateData: { criticalDateData: {...} } }
    const wrappedDate = this.data?.criticalDateData?.criticalDateData;
    if (wrappedDate) {
      return { ...wrappedDate };
    }

    // Handle flat format (from edit flow where service already unwrapped)
    const flatDate = this.data?.criticalDateData;
    if (flatDate && (flatDate.criticalDateNum || flatDate.criticalDateId)) {
      return { ...flatDate };
    }

    // Creating new date - extract entity info
    // Not every caller hands us a whole entityObject — DateDialogService passes the
    // flat { entityId, entityType } pair instead. Reading only entityObject meant
    // that path produced no entityType, so no entityLink, so a notification with
    // nothing to click. Both shapes are on CriticalDateDialogData; accept either.
    const entity = this.data.entityObject;
    const entityType = entity?.entityType || this.data.entityType || '';
    // People (contactNum) and Users (userNum) were missing here, so a date added
    // on a Person went out with no entityId and the backend refused it (400
    // "Missing required fields: parentEntityId") — the date never saved.
    const entityId = entity?.entityId || entity?.contractId || entity?.vendorNum || entity?.baaId || entity?.coiId
      || entity?.contactNum || entity?.userNum || this.data.entityId || '';
    const entityNum = entity?.contractNum || entity?.vendorNum || entity?.baaNum || entity?.coiNum
      || entity?.contactNum || entity?.userNum || this.data.entityNumber || '';

    // Generate entityLink using entityNum (partition key) since routes expect it
    const entityLink = this.generateEntityLink(entityType, entityNum || entityId);

    return {
      criticalDateNum: (new Date()).getTime().toString(),
      type: '',
      name: '',
      eventDate: null, // Your interface uses string | null
      reminderDate: null, // Your interface uses string | null
      assignedTo: [], // Array of user emails assigned to this date
      assignedBy: '', // Will be set to current user (required)
      description: '',
      entityType: entityType,
      entityId: entityId,
      entityNum: entityNum,
      entityName: this.entityDisplayName(entity) || this.data.entityName || '',
      entityLink: entityLink, // Add the generated link
      emailNotificationEnabled: false,
      emailRecipients: [],
      calendarNotificationEnabled: false,
      actionCompleted: false,
      actionSummary: '',
      // Add other optional properties that your interface expects
      owner: '', // Your interface has this
      requiredAction: '' // Your interface has this too
    };
  }

  /**
   * The human name of the record a date hangs off. Each type keeps it somewhere
   * different, and a BAA has no name of its own — it's identified by its Business
   * Associate (a party), which is also what the BAA page shows.
   */
  private entityDisplayName(entity: any): string {
    if (!entity) return '';
    const partyName = (p: any): string =>
      p && typeof p === 'object' ? (p.name || p.vendorData?.name || p.friendlyName || '') : '';
    return (
      entity.nameOfContract ||
      entity.name ||
      [entity.firstName, entity.lastName].filter(Boolean).join(' ') ||
      entity.businessAssociateNameFriendlyName ||
      partyName(entity.businessAssociateName) ||
      entity.insuredPartyFriendlyName ||
      entity.partyProvidingCoiFriendlyName ||
      ''
    ).trim();
  }

  /**
   * Path to the source entity, for linking a date back to what it hangs off.
   * The lookup lives in shared/utils/entity-route.ts — see the note there on why
   * it is no longer copied per call site.
   */
  private generateEntityLink(entityType: string, entityId: string): string {
    const path = entityPathFor(entityType, entityId);
    if (!path && entityType) {
      console.warn(`Unknown entity type for link generation: ${entityType}`);
    }
    return path;
  }

  async ngOnInit(): Promise<void> {
    // Load current user
    this.usersService.currentUserEmail$.subscribe(email => {
      this.currentUserEmail = email;
      if (email && !this.currentCriticalDate.assignedBy) {
        this.currentCriticalDate.assignedBy = email;
      }
    });

    // Load all users
    this.usersService.allUsers$.subscribe(users => {
      this.allUsers = users || [];
    });

    // Load and merge date types
    await this.loadDateTypes();


    // Setup the filtered observable AFTER data is loaded
    this.setupFilteredDateTypes();

    // If editing an existing date, populate the typeControl with the existing type
    if (this.currentCriticalDate.type) {
      this.typeControl.setValue(this.currentCriticalDate.type);
    }

    // If current user isn't loaded yet, trigger refresh
    if (!this.currentUserEmail) {
      this.usersService.refreshAllUsersRecords();
    }
  }


  onTypeInput(event: any): void {
    // This handles the input event for filtering
    const value = event.target.value;
      }

  displayFn(value: string): string {
    // This determines how the selected value is displayed in the input
    return value || '';
  }


  private setupFilteredDateTypes(): void {
        
    this.filteredDateTypes = this.typeControl.valueChanges.pipe(
      startWith(''),
      map(value => {
                const filtered = this.filterDateTypes(value || '');
                return filtered;
      })
    );
  }


  private async loadDateTypes(): Promise<void> {
    try {

      const [importantDateTypes, taskingTypes] = await Promise.all([
        firstValueFrom(this.dateSheetsService.getImportantDateTypes()),
        firstValueFrom(this.dateSheetsService.getTaskingTypes())
      ]);

      console.log('*** importantDateTypes:', importantDateTypes);
      console.log('*** taskingTypes:', taskingTypes);

      // Ensure we have arrays - the API returns arrays directly, not wrapped
      const importantArray = Array.isArray(importantDateTypes) ? importantDateTypes : [];
      const taskingArray = Array.isArray(taskingTypes) ? taskingTypes : [];

      // Merge and deduplicate
      const merged = [...importantArray, ...taskingArray];
      this.allDateTypes = [...new Set(merged)].sort();

      console.log('*** FINAL allDateTypes:', this.allDateTypes);
      console.log('*** FINAL allDateTypes length:', this.allDateTypes.length);

    } catch (error) {
      console.error('*** Failed to load date types:', error);
      this.allDateTypes = [];
    }
  }


  private filterDateTypes(value: string): string[] {

    if (!this.allDateTypes || this.allDateTypes.length === 0) {
            return [];
    }

    if (!value || value.trim() === '') {
      // Return all date types when no filter is applied
      return this.allDateTypes;
    }

    const filterValue = value.toLowerCase().trim();
    const filtered = this.allDateTypes.filter(type =>
      typeof type === 'string' && type.toLowerCase().includes(filterValue)
    );

        return filtered;
  }


  onTypeSelected(): void {
    const selectedType = this.typeControl.value;
    if (selectedType) {
      this.currentCriticalDate.type = selectedType;

      // Name = "<Date Type> - <record name>" (Ray, 2026-10-11). This used to read
      // entityObject.name only, which just Parties have — Contracts, BAAs, COIs and
      // People got the bare type, and so did every reminder email built from it.
      const entityName = this.entityDisplayName(this.data.entityObject) || this.currentCriticalDate.entityName || '';
      this.currentCriticalDate.name = entityName ? `${selectedType} - ${entityName}` : selectedType;

      // Clear the search input after selection
      this.typeSearchControl.setValue('', { emitEvent: false });

      // Trigger validation to update error messages
      this.validateFormOnChange();
    }
  }


  // Date conversion methods for handling string/Date conversion
  getEventDateAsDate(): Date | null {
    if (!this.currentCriticalDate.eventDate) return null;
    if (typeof this.currentCriticalDate.eventDate === 'string') {
      return new Date(this.currentCriticalDate.eventDate);
    }
    return this.currentCriticalDate.eventDate as any;
  }

  getReminderDateAsDate(): Date | null {
    if (!this.currentCriticalDate.reminderDate) return null;
    if (typeof this.currentCriticalDate.reminderDate === 'string') {
      return new Date(this.currentCriticalDate.reminderDate);
    }
    return this.currentCriticalDate.reminderDate as any;
  }

  onEventDateChange(event: any): void {
    // Convert Date object to ISO string for storage
    const date = event.value;
    this.currentCriticalDate.eventDate = date ? date.toISOString() : null;

    // Moving the date moves the series with it: the anchor means "the occurrence
    // on screen", so a stale anchor plus a backend-written count would compute
    // the next occurrence from a date the user has just replaced.
    const recurrence = this.currentCriticalDate.recurrence;
    if (recurrence?.enabled) {
      this.currentCriticalDate.recurrence = anchorTo(recurrence, this.currentCriticalDate.eventDate);
    }
  }

  onReminderDateChange(event: any): void {
    // Convert Date object to ISO string for storage
    const date = event.value;
    this.currentCriticalDate.reminderDate = date ? date.toISOString() : null;
    // Reset reminderSent so the backend sends a new reminder on the new date
    this.currentCriticalDate.reminderSent = false;
  }

  /**
   * Repeat configuration changed.
   *
   * A recurring date that has already had its reminder sent must be re-armed:
   * the backend roll-forward clears `reminderSent` when it advances the date,
   * but a user switching repeat ON for an existing, already-reminded date is
   * asking for future reminders too, and nothing else would ever reset it.
   */
  onRecurrenceChange(recurrence: DateRecurrence | null): void {
    this.currentCriticalDate.recurrence = recurrence ?? undefined;
    if (recurrence?.enabled) this.recurrenceAutoDisabled = false;
    if (recurrence?.enabled && this.currentCriticalDate.reminderSent) {
      this.currentCriticalDate.reminderSent = false;
    }
    this.validateFormOnChange();
  }

  /** Plain-English summary of the repeat, for the form's warning block. */
  get recurrenceSummary(): string {
    return describeRecurrence(this.currentCriticalDate.recurrence, this.currentCriticalDate.eventDate);
  }

  /**
   * Repeating is only offered while something will announce each occurrence.
   *
   * Repeat does not remind anyone by itself — the backend rolls `eventDate`
   * forward and re-arms the reminder. Email and the calendar entry (re-sent on
   * each roll, so it moves) are the only ways anyone hears about it; this app
   * has no in-app reminders. With both off the date just quietly moves.
   */
  get hasNotification(): boolean {
    return this.currentCriticalDate.emailNotificationEnabled === true
      || this.currentCriticalDate.calendarNotificationEnabled === true;
  }

  /** Set when the gate switched Repeat off, so the form can say why. */
  recurrenceAutoDisabled = false;

  /** Notification checkbox changed: re-validate, then re-apply the gate. */
  onNotificationChange(): void {
    this.enforceRecurrenceNeedsNotification();
    this.validateFormOnChange();
  }

  /**
   * Turn Repeat off when no notification is on. Keeps the frequency and end date
   * (`enabled:false`, same as the editor's own toggle-off), so switching a
   * notification back on and re-enabling restores the user's configuration.
   */
  private enforceRecurrenceNeedsNotification(): void {
    const recurrence = this.currentCriticalDate.recurrence;
    if (!this.hasNotification && recurrence?.enabled) {
      this.currentCriticalDate.recurrence = { ...recurrence, enabled: false };
      this.recurrenceAutoDisabled = true;
    }
  }

  /**
   * Stamp WHEN an Important Date was completed, not just that it was.
   *
   * `completionDate` has been on the interface all along and nothing has ever
   * written it — a completed date records no time at all. That was survivable
   * while a date was completed once and finished; with recurrence the backend
   * archives each completed occurrence as it rolls, and an archive entry with no
   * timestamp cannot answer "when was this last done", which is the only
   * question the archive exists to answer.
   */
  onActionCompletedChange(completed: boolean): void {
    this.currentCriticalDate.completionDate = completed ? new Date().toISOString() : undefined;
  }

  getUserFriendlyName(email: string): string {
    const user = this.allUsers.find(u => u.email === email);
    return user ? user.friendlyName : email;
  }




  private validateForm(): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Required field validations
    if (!this.currentCriticalDate.type?.trim()) {
      errors.push('Date type is required');
    }

    if (!this.currentCriticalDate.eventDate) {
      errors.push('Important date is required');
    }

    if (!this.currentCriticalDate.description?.trim()) {
      errors.push('Description/required action is required');
    }

    if (!this.currentCriticalDate.assignedBy?.trim()) {
      errors.push('Assigned By is required');
    }

    // Date logic validations
    if (this.currentCriticalDate.eventDate && this.currentCriticalDate.reminderDate) {
      const eventDate = new Date(this.currentCriticalDate.eventDate);
      const reminderDate = new Date(this.currentCriticalDate.reminderDate);

      if (reminderDate > eventDate) {
        errors.push('Reminder date cannot be after the important date');
      }
    }

    // A repeat whose end date precedes the next occurrence would never fire once —
    // silently, since nothing else in the app would report it.
    const recurrence = this.currentCriticalDate.recurrence;
    if (recurrence?.enabled && this.currentCriticalDate.eventDate && recurrence.endsOn
        && !nextOccurrence(this.currentCriticalDate.eventDate, recurrence)) {
      errors.push('The repeat ends before the next occurrence — extend the end date or turn off Repeat');
    }

    // Update component state
    this.formErrors = errors;
    this.hasValidationErrors = errors.length > 0;

    return {
      isValid: errors.length === 0,
      errors
    };
  }



  validateFormOnChange(): void {
    this.validateForm();
  }

  

  async onSubmit(): Promise<void> {
    const validation = this.validateForm();
    
    if (!validation.isValid) {
      // Errors are now displayed in template, no need for console.error
      return;
    }

    this.waiting = true;

    try {
      // Ensure assignedTo is an array (even if empty)
      if (!Array.isArray(this.currentCriticalDate.assignedTo)) {
        this.currentCriticalDate.assignedTo = [];
      }

      // Sync description to requiredAction so both fields are populated
      if (this.currentCriticalDate.description && !this.currentCriticalDate.requiredAction) {
        this.currentCriticalDate.requiredAction = this.currentCriticalDate.description;
      }

      // A notification toggle with no reminderDate is a promise we cannot keep: the
      // nightly reminder job selects on attribute_exists(reminderDate), so a null one
      // is never picked up and the mail never sends — silently, with no error anywhere.
      // It has already happened on live Crestwood data (CD-IBE44912: four recipients,
      // due 2026-10-01, could never fire).
      //
      // Default to the event date rather than blocking the save. The user ticked the
      // box, which IS the request to be reminded, so "no reminder at all" would make
      // the toggle a lie; and a reminder AFTER the event is already rejected by
      // validateForm, so the event date is the latest useful value. The backend adds
      // the same fallback as a second guard (same belt-and-braces as entityLink).
      const wantsNotification = this.currentCriticalDate.emailNotificationEnabled
                                || this.currentCriticalDate.calendarNotificationEnabled;
      if (wantsNotification && !this.currentCriticalDate.reminderDate && this.currentCriticalDate.eventDate) {
        this.currentCriticalDate.reminderDate = this.currentCriticalDate.eventDate;
      }

      // Save to backend
      const savedDate = await firstValueFrom(
        this.criticalDatesService.createCriticalDate(this.currentCriticalDate)
      );

      // Send assignment email notifications (fire-and-forget)
      this.sendAssignmentNotifications();

      // Ask the backend to send the calendar (.ics) entry. Fire-and-forget, and
      // UNCONDITIONAL — the endpoint re-reads the row and decides whether to send,
      // comparing a fingerprint of the recipient-visible fields. Checking
      // `calendarNotificationEnabled` here would duplicate a rule that already lives
      // server-side, and we cannot tell "changed" from "unchanged" anyway: the form
      // does not hold the pre-edit values.
      //
      // Deliberately AFTER the save resolved and never awaited. A calendar entry is a
      // side effect of a save that has already succeeded; letting it fail the save
      // would turn a missing .ics into a lost Important Date. The service swallows
      // its own errors for the same reason.
      //
      // The reminder-time send is separate and unaffected — it still goes out from the
      // 06:00 job, and this send must not consume `reminderSent`.
      if (savedDate?.criticalDateId) {
        this.criticalDatesService.sendCalendarNotification(savedDate.criticalDateId).subscribe();
      }

      this.dialogRef.close(savedDate);

    } catch (error) {
      console.error('Failed to save critical date:', error);
      // Add server error to form errors
      this.formErrors = ['Failed to save. Please try again.'];
      this.hasValidationErrors = true;
    } finally {
      this.waiting = false;
      // Zoneless app: without this, a failed save showed no error and the
      // spinner state never cleared — it looked like the button did nothing.
      this.cdrZoneless.markForCheck();
    }
  }


  clearErrors(): void {
    this.formErrors = [];
    this.hasValidationErrors = false;
  }

  // Method to check if form is valid (for button state)
  isFormValid(): boolean {
    const validation = this.validateForm();
    return validation.isValid;
  }

  cancelSubmit(): void {
    this.dialogRef.close(null);
  }

  /** Send email notifications to all assigned users (fire-and-forget) */
  private sendAssignmentNotifications(): void {
    const cd = this.currentCriticalDate;

    if (!cd.emailNotificationEnabled) return;
    if (!cd.assignedTo || cd.assignedTo.length === 0) return;

    const assignedByName = this.getUserDisplayName(cd.assignedBy);
    const eventDateFormatted = cd.eventDate
      ? new Date(cd.eventDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
      : 'Not set';
    // Rebuild the path rather than trusting the stored one. `entityLink` is written
    // once at create time and never refreshed, so every record saved before the
    // route map above was fixed carries a blank one; regenerating here repairs them
    // with no data migration. hc-all-orgs-scheduled-ops applies the same fallback on
    // its side, so the reminder email and the .ics agree with this one.
    const storedPath = cd.entityLink || '';
    const entityPath = storedPath || this.generateEntityLink(cd.entityType || '', cd.entityNum || cd.entityId || '');

    // linkToDeployment already ends in /securehome/ and entityPath begins with it.
    // Joining them raw produced .../securehome//securehome/contract/X, which matches
    // no route, falls through to the '**' catch-all and lands the recipient on My
    // Work instead of the record they were sent. Strip one copy — byte-identical to
    // what the backend does, so both mails resolve to the same URL.
    const entityLink = !entityPath
      ? ''
      : entityPath.startsWith('http')
        ? entityPath
        : environment.linkToDeployment + entityPath.replace(/^\/securehome\//, '');

    for (const recipientEmail of cd.assignedTo) {
      const htmlBody = `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <div style="background: #1976d2; color: white; padding: 20px 24px; border-radius: 8px 8px 0 0;">
            <h2 style="margin: 0; font-size: 20px;">A Critical Date Has Been Assigned to You</h2>
          </div>
          <div style="border: 1px solid #e0e0e0; border-top: none; padding: 24px; border-radius: 0 0 8px 8px;">
            <table style="width: 100%; border-collapse: collapse; margin-bottom: 20px;">
              <tr>
                <td style="padding: 8px 0; color: #666; width: 140px; vertical-align: top;">Name</td>
                <td style="padding: 8px 0; font-weight: 500;">${this.htmlText(cd.name || cd.type, 'N/A')}</td>
              </tr>
              <!-- Name, not Date Type (Ray, 2026-10-11). Name is now "Date Type - record name"
                   for every record type, so it carries the type and says what the date is about.
                   On 2026-10-02 the Name row was dropped because name and type held the SAME
                   string on most records (only Parties got a suffix) and the email showed it
                   twice; that duplication is what this change fixes at the source. Older dates
                   whose name equals their type still read correctly.

                   hc-all-orgs-scheduled-ops builds the reminder and calendar titles and was
                   switched to prefer type at the same time; it is being asked to prefer name
                   again so this mail and the reminder for the same record keep matching titles.

                   NOTE: this comment lives inside a template literal. No backticks, and no
                   dollar-brace, or the string terminates and the file stops compiling. -->
              <tr>
                <td style="padding: 8px 0; color: #666; vertical-align: top;">Due Date</td>
                <td style="padding: 8px 0; font-weight: 500;">${eventDateFormatted}</td>
              </tr>
              <tr>
                <td style="padding: 8px 0; color: #666; vertical-align: top;">Required Action or Comments</td>
                <td style="padding: 8px 0;">${this.htmlText(cd.description || cd.requiredAction, 'N/A')}</td>
              </tr>
              ${cd.actionSummary ? `
              <tr>
                <td style="padding: 8px 0; color: #666; vertical-align: top;">Action Summary / Notes</td>
                <td style="padding: 8px 0;">${this.htmlText(cd.actionSummary)}</td>
              </tr>` : ''}
              <tr>
                <td style="padding: 8px 0; color: #666; vertical-align: top;">Assigned By</td>
                <td style="padding: 8px 0;">${this.htmlText(assignedByName)}</td>
              </tr>
              ${cd.entityName ? `
              <tr>
                <td style="padding: 8px 0; color: #666; vertical-align: top;">Related To</td>
                <td style="padding: 8px 0;">${this.htmlText(cd.entityName)}</td>
              </tr>` : ''}
            </table>
            ${entityLink ? `
            <div style="text-align: center; margin-top: 24px;">
              <a href="${entityLink}" style="background: #1976d2; color: white; padding: 12px 32px; text-decoration: none; border-radius: 6px; font-weight: 500; display: inline-block;">
                View Critical Date
              </a>
            </div>` : ''}
            <p style="color: #999; font-size: 12px; margin-top: 24px; text-align: center;">
              This is an automated notification from Healthy Contracts.
            </p>
          </div>
        </div>
      `;

      this.emailService.send({
        to: recipientEmail,
        subject: `Critical Date Assigned: ${cd.name || cd.type || 'Important Date'} - Due ${eventDateFormatted}`,
        htmlBody,
        category: 'important-dates'
      }).subscribe({
        next: () => console.log(`[CriticalDate] Assignment email sent to ${recipientEmail}`),
        error: (err) => console.error(`[CriticalDate] Failed to send assignment email to ${recipientEmail}:`, err)
      });
    }
  }

  /**
   * Renders a user-typed value as email HTML.
   *
   * Everything on an Important Date is free text someone typed into a form, and
   * it lands in a `<td>` by string interpolation — so it needs two things the
   * raw value doesn't give us:
   *
   *  - **Escaping.** An ampersand or an angle bracket in a contract name would
   *    otherwise render as broken markup, and a `<` opens the door to injecting
   *    arbitrary HTML into a mail we send on the user's behalf.
   *  - **Line breaks.** "Required Action or Comments" and "Action Summary" are
   *    textareas, so people press Enter in them. HTML collapses those newlines,
   *    which turns a tidy three-line checklist into one run-on paragraph.
   *
   * Returns `fallback` for an empty value, so callers can choose between 'N/A'
   * and omitting the row entirely.
   */
  private htmlText(value: string | null | undefined, fallback: string = ''): string {
    if (value === null || value === undefined || value === '') return fallback;
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
      .replace(/\r?\n/g, '<br>');
  }

  /** Get a display name for a user email from the cached users list */
  private getUserDisplayName(email: string | undefined): string {
    if (!email) return 'Unknown';
    const user = this.allUsers.find(u => u.email === email);
    return user?.friendlyName || user?.email || email;
  }
}